import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  CreditCard, 
  Search, 
  Plus, 
  Eye, 
  Edit2, 
  Trash2, 
  Share2, 
  Download, 
  Printer, 
  Check, 
  X, 
  AlertCircle, 
  CheckCircle2, 
  Building2, 
  Navigation,
  Calendar,
  DollarSign,
  Receipt,
  RotateCcw
} from 'lucide-react';

export default function CustomerPaymentReceipts({
  currentTenant,
  tenant,
  customers = [],
  centers = [],
  lines = [],
  routes = [],
  currentUser,
  user,
  lang = 'mr'
}) {
  const isMr = lang === 'mr';
  const effectiveTenant = currentTenant || tenant;
  const effectiveUser = currentUser || user || {};
  const tenantSlug = effectiveTenant?.slug || 'samarth-krupa-dairy';
  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

  // Filters at top (Center & Line) - Default 'ALL' (सगळे दिसावेत)
  const [selectedCenterId, setSelectedCenterId] = useState('ALL');
  const [selectedLineId, setSelectedLineId] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Internal centers and lines state (with live fallback if empty)
  const [internalCenters, setInternalCenters] = useState(centers || []);
  const [internalLines, setInternalLines] = useState(lines || routes || []);

  useEffect(() => {
    if (centers && centers.length > 0) setInternalCenters(centers);
    if (lines && lines.length > 0) setInternalLines(lines);
    else if (routes && routes.length > 0) setInternalLines(routes);
  }, [centers, lines, routes]);

  useEffect(() => {
    if ((!centers || centers.length === 0) && tenantSlug) {
      fetch(`${API_BASE}/tenant/${tenantSlug}/centers-lines`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            if (Array.isArray(data.centers) && data.centers.length > 0) setInternalCenters(data.centers);
            if (Array.isArray(data.lines) && data.lines.length > 0) setInternalLines(data.lines);
          }
        })
        .catch(() => {});
    }
  }, [tenantSlug, centers, API_BASE]);

  // Available lines filtered by selected center
  const availableLines = useMemo(() => {
    if (selectedCenterId === 'ALL') return internalLines;
    return internalLines.filter(l => String(l.center_id) === String(selectedCenterId));
  }, [internalLines, selectedCenterId]);

  // Customers filtered by Center and Line for dropdown
  const filteredCustomerOptions = useMemo(() => {
    return customers.filter(c => {
      let matchCenter = true;
      let matchLine = true;
      if (selectedLineId !== 'ALL') {
        matchLine = String(c.line_id) === String(selectedLineId);
      }
      if (selectedCenterId !== 'ALL') {
        const lineObj = internalLines.find(l => String(l.id) === String(c.line_id));
        matchCenter = lineObj ? String(lineObj.center_id) === String(selectedCenterId) : false;
      }
      return matchCenter && matchLine;
    });
  }, [customers, selectedCenterId, selectedLineId, internalLines]);

  // Form State
  const getLocalDateStr = () => {
    const d = new Date();
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return yr + '-' + mo + '-' + da;
  };
  const todayStr = getLocalDateStr();
  const initialFormState = {
    id: null, // null for new, number for edit
    receipt_no: '',
    payment_date: todayStr,
    customer_id: '',
    customer_name: '',
    line_name: '',
    center_name: '',
    phone: '',
    cash_amount: '',
    upi_amount: '',
    cheque_amount: '',
    cheque_bank_name: '',
    cheque_date: todayStr,
    cheque_no: '',
    remarks: '',
    dues_at_payment: 0,
    prev_month_dues: 0,
    current_month_milk_amount: 0
  };

  const [formData, setFormData] = useState(initialFormState);
  const [duesLoading, setDuesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  // Saved Receipts List State
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // View Receipt Modal State
  const [viewReceipt, setViewReceipt] = useState(null);

  // Fetch payments from API
  const fetchPayments = useCallback(async () => {
    setLoadingPayments(true);
    try {
      let url = `${API_BASE}/tenant/${tenantSlug}/customer-payments?`;
      if (selectedCenterId !== 'ALL') url += `center_id=${selectedCenterId}&`;
      if (selectedLineId !== 'ALL') url += `line_id=${selectedLineId}&`;

      const res = await fetch(url);
      const data = await res.json();
      if (data.success && Array.isArray(data.payments)) {
        setPayments(data.payments);
      }
    } catch (err) {
      console.warn('Failed to fetch customer payments:', err);
    } finally {
      setLoadingPayments(false);
    }
  }, [API_BASE, tenantSlug, selectedCenterId, selectedLineId]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Live Dues Fetcher for selected Customer & Date
  const fetchCustomerDues = useCallback(async (customerId, date) => {
    if (!customerId) return;
    setDuesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/customers/${customerId}/dues?date=${date || todayStr}`);
      const data = await res.json();
      if (data.success) {
        setFormData(prev => ({
          ...prev,
          dues_at_payment: parseFloat(data.total_dues || 0),
          prev_month_dues: parseFloat(data.prev_month_dues || 0),
          current_month_milk_amount: parseFloat(data.current_month_milk_amount || 0)
        }));
      }
    } catch (err) {
      // Fallback to customer's static balance if API fails
      const cust = customers.find(c => String(c.id) === String(customerId));
      const bal = parseFloat(cust?.balance || 0);
      setFormData(prev => ({
        ...prev,
        dues_at_payment: bal,
        prev_month_dues: bal,
        current_month_milk_amount: 0
      }));
    } finally {
      setDuesLoading(false);
    }
  }, [API_BASE, tenantSlug, todayStr, customers]);

  // Handle Customer Selection by Dropdown
  const handleSelectCustomer = (custId) => {
    if (!custId) {
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: '',
        line_name: '',
        center_name: '',
        phone: '',
        dues_at_payment: 0,
        prev_month_dues: 0,
        current_month_milk_amount: 0
      }));
      return;
    }

    const cust = customers.find(c => String(c.id) === String(custId));
    if (cust) {
      const lineObj = internalLines.find(l => String(l.id) === String(cust.line_id));
      const centerObj = internalCenters.find(cn => String(cn.id) === String(lineObj?.center_id));
      
      setFormData(prev => ({
        ...prev,
        customer_id: cust.id,
        customer_name: isMr ? (cust.name_mr || cust.name || `ग्राहक #${cust.id}`) : (cust.name_en || cust.name || `Customer #${cust.id}`),
        line_name: lineObj ? (isMr ? (lineObj.name_mr || lineObj.name) : (lineObj.name_en || lineObj.name)) : (cust.line_name || ''),
        center_name: centerObj ? (isMr ? (centerObj.name_mr || centerObj.name) : (centerObj.name_en || centerObj.name)) : '',
        phone: cust.phone || ''
      }));

      fetchCustomerDues(cust.id, formData.payment_date);
    }
  };

  // Handle Customer ID Input (Synchronized two-way)
  const handleCustomerIdInput = (val) => {
    setFormData(prev => ({ ...prev, customer_id: val }));
    const match = customers.find(c => String(c.id) === String(val.trim()));
    if (match) {
      handleSelectCustomer(match.id);
    }
  };

  // When payment date changes, refresh dues if customer is selected
  const handleDateChange = (newDate) => {
    setFormData(prev => ({ ...prev, payment_date: newDate }));
    if (formData.customer_id) {
      fetchCustomerDues(formData.customer_id, newDate);
    }
  };

  // Computed Totals
  const numCash = parseFloat(formData.cash_amount) || 0;
  const numUpi = parseFloat(formData.upi_amount) || 0;
  const numCheque = parseFloat(formData.cheque_amount) || 0;
  const totalReceived = parseFloat((numCash + numUpi + numCheque).toFixed(2));
  const remainingBalance = Math.max(0, parseFloat((formData.dues_at_payment - totalReceived).toFixed(2)));

  // Form Submit (Create or Update)
  const handleSubmitReceipt = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      setStatusMessage({ type: 'error', text: isMr ? 'कृपया ग्राहक निवडा किंवा ID टाका!' : 'Please select customer!' });
      return;
    }
    if (totalReceived <= 0) {
      setStatusMessage({ type: 'error', text: isMr ? 'किमान एका पद्धतीची (रोख, UPI किंवा चेक) रक्कम टाका!' : 'Please enter payment amount!' });
      return;
    }

    setSubmitting(true);
    setStatusMessage(null);

    const payload = {
      customer_id: formData.customer_id,
      payment_date: formData.payment_date,
      cash_amount: numCash,
      upi_amount: numUpi,
      cheque_amount: numCheque,
      cheque_bank_name: numCheque > 0 ? formData.cheque_bank_name : null,
      cheque_date: numCheque > 0 ? formData.cheque_date : null,
      cheque_no: numCheque > 0 ? formData.cheque_no : null,
      dues_at_payment: formData.dues_at_payment,
      prev_month_dues: formData.prev_month_dues,
      remarks: formData.remarks
    };

    try {
      const isEdit = Boolean(formData.id);
      const url = isEdit 
        ? `${API_BASE}/tenant/${tenantSlug}/customer-payments/${formData.id}`
        : `${API_BASE}/tenant/${tenantSlug}/customer-payments`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (data.success) {
        setStatusMessage({
          type: 'success',
          text: isEdit 
            ? (isMr ? 'पेमेंट पावती यशस्वीरित्या अद्ययावत झाली!' : 'Receipt updated successfully!')
            : (isMr ? 'पेमेंट पावती यशस्वीरित्या सेव्ह झाली!' : 'Receipt saved successfully!')
        });

        // Refresh table
        await fetchPayments();

        // Reset form
        setFormData(initialFormState);
      } else {
        setStatusMessage({ type: 'error', text: data.message || 'Error saving receipt' });
      }
    } catch (err) {
      console.error('Save receipt error:', err);
      setStatusMessage({ type: 'error', text: isMr ? 'सर्व्हरशी संपर्क होऊ शकला नाही!' : 'Cannot contact server!' });
    } finally {
      setSubmitting(false);
    }
  };

  // Edit Receipt
  const handleEdit = (p) => {
    const cust = customers.find(c => String(c.id) === String(p.customer_id));
    setFormData({
      id: p.id,
      receipt_no: p.receipt_no,
      payment_date: p.payment_date,
      customer_id: p.customer_id,
      customer_name: isMr ? (p.customer_name_mr || p.customer_name) : (p.customer_name_en || p.customer_name),
      line_name: isMr ? (p.line_name_mr || '') : (p.line_name_en || ''),
      center_name: isMr ? (p.center_name_mr || '') : (p.center_name_en || ''),
      phone: p.customer_phone || cust?.phone || '',
      cash_amount: p.cash_amount > 0 ? p.cash_amount : '',
      upi_amount: p.upi_amount > 0 ? p.upi_amount : '',
      cheque_amount: p.cheque_amount > 0 ? p.cheque_amount : '',
      cheque_bank_name: p.cheque_bank_name || '',
      cheque_date: p.cheque_date || todayStr,
      cheque_no: p.cheque_no || '',
      remarks: p.remarks || '',
      dues_at_payment: parseFloat(p.dues_at_payment || 0),
      prev_month_dues: parseFloat(p.prev_month_dues || 0),
      current_month_milk_amount: Math.max(0, parseFloat(p.dues_at_payment || 0) - parseFloat(p.prev_month_dues || 0))
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Delete Receipt
  const handleDelete = async (p) => {
    const confirmMsg = isMr 
      ? `तुम्हाला खात्री आहे का पावती #${p.receipt_no} (रक्कम ₹${p.total_amount}) डिलीट करायची आहे?`
      : `Are you sure you want to delete receipt #${p.receipt_no} (₹${p.total_amount})?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/customer-payments/${p.id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        setStatusMessage({ type: 'success', text: isMr ? 'पावती यशस्वीरित्या डिलीट केली!' : 'Receipt deleted!' });
        await fetchPayments();
      } else {
        alert(data.message || 'Failed to delete');
      }
    } catch (err) {
      console.error(err);
      alert('Error deleting receipt');
    }
  };

  // Generate SMS Payment Receipt Message
  const generateSmsReceiptText = (p) => {
    const dairyName = isMr 
      ? (effectiveTenant?.brandMarathi || effectiveTenant?.brand_name_mr || effectiveTenant?.name || 'समर्थ कृपा डेअरी')
      : (effectiveTenant?.brandEnglish || effectiveTenant?.brand_name_en || effectiveTenant?.name || 'Samarth Krupa Dairy');

    const [yr, mo, da] = (p.payment_date || todayStr).split('-');
    const formattedDate = `${da}/${mo}/${yr}`;

    const text = isMr ? `${dairyName} - पेमेंट पावती\nपावती क्र: ${p.receipt_no}\nदिनांक: ${formattedDate}\nग्राहक: ${p.customer_name_mr || p.customer_name}\nजमा रक्कम: रु. ${p.total_amount}\nउर्वरित बाकी: रु. ${p.balance_remaining}\nधन्यवाद!`
      : `${dairyName} - Payment Receipt\nReceipt: ${p.receipt_no}\nDate: ${formattedDate}\nCustomer: ${p.customer_name_en || p.customer_name}\nReceived: Rs. ${p.total_amount}\nRemaining Due: Rs. ${p.balance_remaining}\nThanks!`;

    return encodeURIComponent(text);
  };

  // Generate WhatsApp Payment Receipt Message
  const generateWhatsAppReceiptText = (p) => {
    const dairyName = isMr 
      ? (effectiveTenant?.brandMarathi || effectiveTenant?.brand_name_mr || effectiveTenant?.name || 'समर्थ कृपा डेअरी')
      : (effectiveTenant?.brandEnglish || effectiveTenant?.brand_name_en || effectiveTenant?.name || 'Samarth Krupa Dairy');

    const [yr, mo, da] = (p.payment_date || todayStr).split('-');
    const formattedDate = `${da}/${mo}/${yr}`;

    const modes = [];
    if (parseFloat(p.cash_amount) > 0) modes.push(`रोख: ₹${p.cash_amount}`);
    if (parseFloat(p.upi_amount) > 0) modes.push(`UPI: ₹${p.upi_amount}`);
    if (parseFloat(p.cheque_amount) > 0) modes.push(`चेक: ₹${p.cheque_amount}${p.cheque_no ? ` (#${p.cheque_no})` : ''}`);
    const modesText = modes.join(' | ') || 'रोख';

    const text = isMr ? `*॥ ${dairyName} - अधिकृत पेमेंट पावती ॥*
पावती क्र: *${p.receipt_no}*
तारीख: *${formattedDate}*
------------------------------
ग्राहक नाव: *${p.customer_name_mr || p.customer_name}* (ID: ${p.customer_id})
मार्ग: *${p.line_name_mr || p.line_name || 'Line'}*

💵 *मिळालेली रक्कम: ₹${p.total_amount}*
(पद्धत: ${modesText})
------------------------------
उर्वरित बाकी शिल्लक: *₹${p.balance_remaining}*

_आपले पेमेंट यशस्वीरित्या जमा झाले आहे. धन्यवाद!_
_MilkPro Mitra द्वारे डिजिटल पावती_`
: `*॥ ${dairyName} - Official Payment Receipt ॥*
Receipt No: *${p.receipt_no}*
Date: *${formattedDate}*
------------------------------
Customer: *${p.customer_name_en || p.customer_name}* (ID: ${p.customer_id})
Route: *${p.line_name_en || p.line_name || 'Line'}*

💵 *Amount Received: ₹${p.total_amount}*
(Mode: ${modesText})
------------------------------
Remaining Balance Due: *₹${p.balance_remaining}*

_Payment received with thanks!_
_Managed digitally via MilkPro Mitra_`;

    return encodeURIComponent(text);
  };

  // Filtered Payments for Table Display
  const filteredPayments = useMemo(() => {
    return payments.filter(p => {
      const q = (searchQuery || '').trim().toLowerCase();
      const matchSearch = !q || 
        (p.receipt_no || '').toLowerCase().includes(q) ||
        (p.customer_name_mr || '').toLowerCase().includes(q) ||
        (p.customer_name_en || '').toLowerCase().includes(q) ||
        (p.customer_phone || '').includes(q) ||
        String(p.customer_id).includes(q);

      let matchLine = true;
      if (selectedLineId !== 'ALL') {
        matchLine = String(p.line_id) === String(selectedLineId);
      }

      let matchCenter = true;
      if (selectedCenterId !== 'ALL') {
        matchCenter = String(p.center_id) === String(selectedCenterId);
      }

      return matchSearch && matchLine && matchCenter;
    });
  }, [payments, searchQuery, selectedLineId, selectedCenterId]);

  // Aggregate KPI summary for payments
  const totalCollected = filteredPayments.reduce((sum, p) => sum + parseFloat(p.total_amount || 0), 0);
  const totalCashCollected = filteredPayments.reduce((sum, p) => sum + parseFloat(p.cash_amount || 0), 0);
  const totalUpiCollected = filteredPayments.reduce((sum, p) => sum + parseFloat(p.upi_amount || 0), 0);
  const totalChequeCollected = filteredPayments.reduce((sum, p) => sum + parseFloat(p.cheque_amount || 0), 0);

  return (
    <div style={{ padding: '1rem', maxWidth: 1200, margin: '0 auto' }}>
      {/* Top Banner with Center & Route Filter Selectors */}
      <div style={{
        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
        borderRadius: 16,
        padding: '1.25rem',
        color: '#ffffff',
        marginBottom: '1.25rem',
        boxShadow: '0 10px 25px -5px rgba(2, 132, 199, 0.25)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <span style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', background: 'rgba(255,255,255,0.2)', padding: '3px 8px', borderRadius: 6 }}>
              {isMr ? 'ग्राहक वसुली व पावती' : 'Customer Collections & Receipts'}
            </span>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 900, margin: '6px 0 2px 0' }}>
              {isMr ? 'ग्राहक पेमेंट पावती (Receipt Form)' : 'Customer Payment Receipt & Ledger'}
            </h1>
            <p style={{ fontSize: '0.8rem', opacity: 0.9, margin: 0 }}>
              {isMr ? 'रोख, UPI व चेक पेमेंट स्वीकृती, थेट थकबाकी हिशोब व १-क्लिक व्हॉट्सॲप पावती' : 'Cash, UPI & Cheque payments, live dues breakdown & 1-click WhatsApp receipts'}
            </p>
          </div>

          {/* Center & Route Filter Controls */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Center Selector Filter */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: 'rgba(255,255,255,0.18)',
              borderRadius: 8,
              padding: '0 0.5rem',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              <Building2 size={14} style={{ color: '#ffffff', opacity: 0.9, marginRight: 4 }} />
              <select
                value={selectedCenterId}
                onChange={(e) => {
                  setSelectedCenterId(e.target.value);
                  setSelectedLineId('ALL');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  padding: '0.45rem 0.2rem',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                title={isMr ? 'सेंटर निवडा' : 'Select Center'}
              >
                <option value="ALL" style={{ color: '#0f172a' }}>{isMr ? 'सर्व सेंटर्स' : 'All Centers'}</option>
                {internalCenters.map(c => (
                  <option key={c.id} value={c.id} style={{ color: '#0f172a' }}>
                    {isMr ? (c.name_mr || c.name || `सेंटर #${c.id}`) : (c.name_en || c.name || `Center #${c.id}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Route / Line Selector Filter */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: 'rgba(255,255,255,0.18)',
              borderRadius: 8,
              padding: '0 0.5rem',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              <Navigation size={14} style={{ color: '#ffffff', opacity: 0.9, marginRight: 4 }} />
              <select
                value={selectedLineId}
                onChange={(e) => setSelectedLineId(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  padding: '0.45rem 0.2rem',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                title={isMr ? 'मार्ग (Route) निवडा' : 'Select Route/Line'}
              >
                <option value="ALL" style={{ color: '#0f172a' }}>{isMr ? 'सर्व मार्ग (Routes)' : 'All Routes'}</option>
                {availableLines.map(l => (
                  <option key={l.id} value={l.id} style={{ color: '#0f172a' }}>
                    {isMr ? (l.name_mr || l.name || `मार्ग #${l.id}`) : (l.name_en || l.name || `Route #${l.id}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchPayments}
              style={{
                padding: '0.45rem 0.75rem',
                borderRadius: 8,
                border: 'none',
                background: '#ffffff',
                color: '#0284c7',
                fontWeight: 800,
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                cursor: 'pointer'
              }}
            >
              <RotateCcw size={13} />
              <span>{isMr ? 'रिफ्रेश' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        {/* Collection KPI Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: 10,
          marginTop: '1.25rem'
        }}>
          <div style={{ background: 'rgba(255,255,255,0.12)', padding: '0.75rem', borderRadius: 10 }}>
            <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{isMr ? 'एकूण पावत्या' : 'Total Receipts'}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 900 }}>{filteredPayments.length}</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.12)', padding: '0.75rem', borderRadius: 10 }}>
            <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{isMr ? 'एकूण जमा वसुली' : 'Total Collected'}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 900, color: '#a7f3d0' }}>₹{totalCollected.toLocaleString()}</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.12)', padding: '0.75rem', borderRadius: 10 }}>
            <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{isMr ? 'रोख जमा' : 'Cash'}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 900 }}>₹{totalCashCollected.toLocaleString()}</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.12)', padding: '0.75rem', borderRadius: 10 }}>
            <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{isMr ? 'UPI जमा' : 'UPI'}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 900 }}>₹{totalUpiCollected.toLocaleString()}</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.12)', padding: '0.75rem', borderRadius: 10 }}>
            <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{isMr ? 'चेक जमा' : 'Cheque'}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 900 }}>₹{totalChequeCollected.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Main Payment Receipt Entry Form Card */}
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        padding: '1.25rem',
        marginBottom: '1.25rem',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)'
      }}>
        {/* Form Title & Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Receipt size={20} color="#0284c7" />
            <h2 style={{ fontSize: '1.1rem', fontWeight: 900, color: '#0f172a', margin: 0 }}>
              {formData.id 
                ? (isMr ? `✏️ पेमेंट पावती संपादन (Receipt #${formData.receipt_no})` : `✏️ Edit Receipt #${formData.receipt_no}`)
                : (isMr ? '➕ नवीन ग्राहक पेमेंट पावती नोंद' : '➕ Record New Customer Payment Receipt')}
            </h2>
          </div>

          {formData.id && (
            <button
              onClick={() => setFormData(initialFormState)}
              style={{
                padding: '0.35rem 0.75rem',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                background: '#f8fafc',
                color: '#475569',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              {isMr ? 'रद्द करा (नवीन पावती)' : 'Cancel Edit'}
            </button>
          )}
        </div>

        {/* Status Toast Alert */}
        {statusMessage && (
          <div style={{
            padding: '0.65rem 0.85rem',
            borderRadius: 8,
            marginBottom: '1rem',
            fontSize: '0.8rem',
            fontWeight: 700,
            background: statusMessage.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: statusMessage.type === 'success' ? '#15803d' : '#b91c1c',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            {statusMessage.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            <span>{statusMessage.text}</span>
          </div>
        )}

        <form onSubmit={handleSubmitReceipt}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {/* 1. Date */}
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 800, color: '#334155', marginBottom: 4 }}>
                {isMr ? 'पावती तारीख (Date) *' : 'Payment Date *'}
              </label>
              <input
                type="date"
                required
                value={formData.payment_date}
                onChange={(e) => handleDateChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.85rem',
                  outline: 'none',
                  background: '#f8fafc'
                }}
              />
            </div>

            {/* 2. Customer ID (Direct Entry) */}
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 800, color: '#334155', marginBottom: 4 }}>
                {isMr ? 'ग्राहक ID (Customer ID)' : 'Customer ID'}
              </label>
              <input
                type="number"
                placeholder={isMr ? 'उदा. 1' : 'e.g. 1'}
                value={formData.customer_id}
                onChange={(e) => handleCustomerIdInput(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.85rem',
                  outline: 'none'
                }}
              />
            </div>

            {/* 3. Customer Name Dropdown */}
            <div style={{ gridColumn: 'span 2' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#334155' }}>
                  {isMr ? 'ग्राहक निवडा (Customer Name) *' : 'Select Customer *'}
                </label>
                {formData.customer_id && (
                  <button
                    type="button"
                    onClick={() => fetchCustomerDues(formData.customer_id, formData.payment_date)}
                    disabled={duesLoading}
                    title={isMr ? "या पावतीच्या तारखेचा अचूक बॅलेन्स डेटाबेसमधून तपासा" : "Check live balance from DB"}
                    style={{
                      padding: '2px 8px',
                      borderRadius: 6,
                      border: '1px solid #0284c7',
                      background: '#f0f9ff',
                      color: '#0369a1',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    <span>🔄</span>
                    <span>{duesLoading ? (isMr ? 'तपासत आहे...' : 'Checking...') : (isMr ? 'चेक बॅलेन्स' : 'Check Balance')}</span>
                  </button>
                )}
              </div>
              <select
                required
                value={formData.customer_id}
                onChange={(e) => handleSelectCustomer(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.85rem',
                  outline: 'none',
                  background: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <option value="">{isMr ? '-- ग्राहक निवडा --' : '-- Select Customer --'}</option>
                {filteredCustomerOptions.map(c => (
                  <option key={c.id} value={c.id}>
                    #{c.id} - {isMr ? (c.name_mr || c.name) : (c.name_en || c.name)} {c.phone ? `(${c.phone})` : ''} - {c.line_name ? `[${c.line_name}]` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Dues Breakdown Information Box */}
          {formData.customer_id && (
            <div style={{
              background: '#f0f9ff',
              border: '1.5px solid #bae6fd',
              borderRadius: 12,
              padding: '0.85rem 1rem',
              marginTop: '1rem',
              marginBottom: '1rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <span style={{ fontSize: '0.72rem', color: '#0369a1', fontWeight: 800, textTransform: 'uppercase' }}>
                    {isMr ? 'थकबाकी माहिती व हिशोब फोड (Live Dues Breakdown)' : 'Live Dues Breakdown'}
                  </span>
                  <div style={{ fontSize: '1.2rem', fontWeight: 900, color: '#0284c7', marginTop: 2 }}>
                    {isMr ? 'एकूण येणे बाकी:' : 'Total Due:'} ₹{formData.dues_at_payment.toLocaleString()}
                  </div>
                </div>

                <div style={{ background: '#ffffff', padding: '0.45rem 0.85rem', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                  <div style={{ fontSize: '0.74rem', color: '#475569', fontWeight: 700 }}>
                    {isMr ? 'हिशोब तपशील (Split Details):' : 'Split Details:'}
                  </div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a' }}>
                    <span style={{ color: '#b91c1c' }}>
                      {isMr ? `मागील महिना बाकी: ₹${formData.prev_month_dues}` : `Prev Month: ₹${formData.prev_month_dues}`}
                    </span>
                    {' + '}
                    <span style={{ color: '#16a34a' }}>
                      {isMr ? `चालू महिना: ₹${formData.current_month_milk_amount}` : `Current Month: ₹${formData.current_month_milk_amount}`}
                    </span>
                  </div>
                </div>
              </div>

              {duesLoading && (
                <div style={{ fontSize: '0.75rem', color: '#0284c7', marginTop: 4 }}>
                  {isMr ? 'थकबाकी तपासत आहे...' : 'Calculating dues...'}
                </div>
              )}
            </div>
          )}

          {/* Payment Mode Inputs (Cash, UPI, Cheque) */}
          <div style={{ marginTop: '1rem', background: '#f8fafc', padding: '1rem', borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', marginBottom: 10, textTransform: 'uppercase' }}>
              {isMr ? '💵 पेमेंट पद्धत व रक्कम विभागणी (Payment Modes)' : 'Payment Method Breakdown'}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              {/* Cash Amount */}
              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 800, color: '#334155', marginBottom: 4 }}>
                  {isMr ? 'रोख रक्कम (Cash ₹)' : 'Cash (₹)'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={formData.cash_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, cash_amount: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    outline: 'none'
                  }}
                />
              </div>

              {/* UPI Amount */}
              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 800, color: '#334155', marginBottom: 4 }}>
                  {isMr ? 'UPI रक्कम (GPay/PhonePe ₹)' : 'UPI (₹)'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={formData.upi_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, upi_amount: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    outline: 'none'
                  }}
                />
              </div>

              {/* Cheque Amount */}
              <div>
                <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 800, color: '#334155', marginBottom: 4 }}>
                  {isMr ? 'चेक रक्कम (Cheque ₹)' : 'Cheque (₹)'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={formData.cheque_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, cheque_amount: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 8,
                    border: numCheque > 0 ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    outline: 'none',
                    background: numCheque > 0 ? '#eff6ff' : '#ffffff'
                  }}
                />
              </div>
            </div>

            {/* Conditional Cheque Details (Shown if Cheque Amount > 0) */}
            {numCheque > 0 && (
              <div style={{
                marginTop: '1rem',
                background: '#eff6ff',
                border: '1.5px dashed #93c5fd',
                borderRadius: 10,
                padding: '0.85rem'
              }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#1e40af', marginBottom: 8 }}>
                  {isMr ? '🏦 चेक तपशील (Cheque Details - आवश्यक)' : 'Cheque Details (Required)'}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#1e3a8a', marginBottom: 3 }}>
                      {isMr ? 'बँकेचे नाव (Bank Name) *' : 'Bank Name *'}
                    </label>
                    <input
                      type="text"
                      required
                      placeholder={isMr ? 'उदा. बँक ऑफ महाराष्ट्र, SBI, HDFC...' : 'e.g. SBI, HDFC...'}
                      value={formData.cheque_bank_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, cheque_bank_name: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '0.45rem 0.65rem',
                        borderRadius: 6,
                        border: '1px solid #93c5fd',
                        fontSize: '0.82rem',
                        outline: 'none',
                        background: '#ffffff'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#1e3a8a', marginBottom: 3 }}>
                      {isMr ? 'चेक तारीख (Cheque Date) *' : 'Cheque Date *'}
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.cheque_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, cheque_date: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '0.45rem 0.65rem',
                        borderRadius: 6,
                        border: '1px solid #93c5fd',
                        fontSize: '0.82rem',
                        outline: 'none',
                        background: '#ffffff'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#1e3a8a', marginBottom: 3 }}>
                      {isMr ? 'चेक नंबर (Cheque No.) *' : 'Cheque Number *'}
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="उदा. 052418"
                      value={formData.cheque_no}
                      onChange={(e) => setFormData(prev => ({ ...prev, cheque_no: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '0.45rem 0.65rem',
                        borderRadius: 6,
                        border: '1px solid #93c5fd',
                        fontSize: '0.82rem',
                        outline: 'none',
                        background: '#ffffff'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Live Calculation Strip: Total Received & Balance Remaining */}
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: 10,
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10
            }}>
              <div>
                <span style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 700 }}>
                  {isMr ? 'मिळालेली एकूण रक्कम:' : 'Total Received:'}
                </span>
                <span style={{ marginLeft: 8, fontSize: '1.25rem', fontWeight: 900, color: '#16a34a' }}>
                  ₹{totalReceived.toLocaleString()}
                </span>
              </div>

              <div>
                <span style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 700 }}>
                  {isMr ? 'उर्वरित बाकी शिल्लक:' : 'Remaining Balance:'}
                </span>
                <span style={{ marginLeft: 8, fontSize: '1.15rem', fontWeight: 900, color: remainingBalance > 0 ? '#b91c1c' : '#15803d' }}>
                  ₹{remainingBalance.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Remarks input */}
            <div style={{ marginTop: '0.85rem' }}>
              <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#475569', marginBottom: 3 }}>
                {isMr ? 'रिमार्क / टीप (पर्यायी):' : 'Remarks (Optional):'}
              </label>
              <input
                type="text"
                placeholder={isMr ? 'उदा. चालू महिन्याचा पहिला हप्ता...' : 'e.g. Partial payment...'}
                value={formData.remarks}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.45rem 0.75rem',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.82rem',
                  outline: 'none',
                  background: '#ffffff'
                }}
              />
            </div>
          </div>

          {/* Form Action Buttons */}
          <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            {formData.id && (
              <button
                type="button"
                onClick={() => setFormData(initialFormState)}
                style={{
                  padding: '0.55rem 1.25rem',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                {isMr ? 'रद्द करा' : 'Cancel'}
              </button>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '0.6rem 1.5rem',
                borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '0.9rem',
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)'
              }}
            >
              <Check size={16} />
              <span>
                {submitting 
                  ? (isMr ? 'पावती सेव्ह होत आहे...' : 'Saving Receipt...') 
                  : formData.id 
                    ? (isMr ? '💾 बदल जतन करा (Update Receipt)' : 'Update Receipt')
                    : (isMr ? '💾 पावती सेव्ह करा (Save Receipt)' : 'Save Receipt')}
              </span>
            </button>
          </div>
        </form>
      </div>

      {/* Filter and Search Bar for Saved Receipts */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        padding: '0.75rem 1rem',
        marginBottom: '1rem',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
      }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder={isMr ? 'पावती शोधा (पावती क्र, ग्राहक नाव, फोन)...' : 'Search receipts...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.45rem 0.75rem 0.45rem 2.2rem',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          />
        </div>

        <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#475569' }}>
          {isMr ? `एकूण ${filteredPayments.length} पावत्या` : `${filteredPayments.length} Receipts`}
        </div>
      </div>

      {/* Saved Receipts Ledger Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', fontWeight: 800 }}>
                <th style={{ padding: '0.85rem 1rem' }}>#</th>
                <th style={{ padding: '0.85rem 1rem' }}>{isMr ? 'पावती क्र.' : 'Receipt #'}</th>
                <th style={{ padding: '0.85rem 1rem' }}>{isMr ? 'तारीख' : 'Date'}</th>
                <th style={{ padding: '0.85rem 1rem' }}>{isMr ? 'ग्राहक नाव व ID' : 'Customer'}</th>
                <th style={{ padding: '0.85rem 1rem' }}>{isMr ? 'मार्ग व सेंटर' : 'Route & Center'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{isMr ? 'रोख' : 'Cash'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{isMr ? 'UPI' : 'UPI'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{isMr ? 'चेक' : 'Cheque'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{isMr ? 'एकूण जमा' : 'Total Paid'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{isMr ? 'शिल्लक बाकी' : 'Balance'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>{isMr ? 'कृती (Actions)' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {loadingPayments ? (
                <tr>
                  <td colSpan={11} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                    {isMr ? 'पेमेंट पावत्या लोड होत आहेत...' : 'Loading payment receipts...'}
                  </td>
                </tr>
              ) : filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                    {isMr ? 'कोणत्याही पेमेंट पावत्या सापडल्या नाहीत' : 'No payment receipts found'}
                  </td>
                </tr>
              ) : (
                filteredPayments.map((p, idx) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.85rem 1rem', color: '#94a3b8', fontWeight: 700 }}>{idx + 1}</td>
                    <td style={{ padding: '0.85rem 1rem', fontWeight: 800, color: '#0284c7', fontFamily: 'monospace' }}>
                      {p.receipt_no}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', color: '#475569' }}>
                      {p.payment_date}
                    </td>
                    <td style={{ padding: '0.85rem 1rem' }}>
                      <div style={{ fontWeight: 800, color: '#0f172a' }}>
                        {isMr ? (p.customer_name_mr || p.customer_name) : (p.customer_name_en || p.customer_name)}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        ID: #{p.customer_id} {p.customer_phone ? `• ${p.customer_phone}` : ''}
                      </div>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', color: '#475569' }}>
                      <span style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 }}>
                        {p.line_name_mr || p.line_name || 'Line'}
                      </span>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: 700 }}>
                      {parseFloat(p.cash_amount) > 0 ? `₹${p.cash_amount}` : '-'}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: 700 }}>
                      {parseFloat(p.upi_amount) > 0 ? `₹${p.upi_amount}` : '-'}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>
                      {parseFloat(p.cheque_amount) > 0 ? (
                        <div>
                          <div style={{ fontWeight: 800, color: '#1e40af' }}>₹{p.cheque_amount}</div>
                          <div style={{ fontSize: '0.68rem', color: '#64748b' }}>
                            {p.cheque_bank_name} {p.cheque_no ? `(#${p.cheque_no})` : ''}
                          </div>
                        </div>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: 900, color: '#15803d', fontSize: '0.92rem' }}>
                      ₹{p.total_amount}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', color: p.balance_remaining > 0 ? '#b91c1c' : '#15803d', fontWeight: 700 }}>
                      ₹{p.balance_remaining}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                        {/* 1. View Printable Receipt */}
                        <button
                          onClick={() => setViewReceipt(p)}
                          title={isMr ? 'पावती पहा व प्रिंट करा' : 'View & Print Receipt'}
                          style={{
                            padding: '0.35rem 0.55rem',
                            borderRadius: 6,
                            border: '1px solid #cbd5e1',
                            background: '#f8fafc',
                            cursor: 'pointer',
                            color: '#0f172a'
                          }}
                        >
                          <Eye size={13} />
                        </button>

                        {/* 2. Edit Receipt */}
                        <button
                          onClick={() => handleEdit(p)}
                          title={isMr ? 'पावती संपादित करा' : 'Edit Receipt'}
                          style={{
                            padding: '0.35rem 0.55rem',
                            borderRadius: 6,
                            border: '1px solid #bfdbfe',
                            background: '#eff6ff',
                            cursor: 'pointer',
                            color: '#1d4ed8'
                          }}
                        >
                          <Edit2 size={13} />
                        </button>

                        {/* 3. Delete Receipt */}
                        <button
                          onClick={() => handleDelete(p)}
                          title={isMr ? 'पावती डिलीट करा' : 'Delete Receipt'}
                          style={{
                            padding: '0.35rem 0.55rem',
                            borderRadius: 6,
                            border: '1px solid #fecaca',
                            background: '#fef2f2',
                            cursor: 'pointer',
                            color: '#dc2626'
                          }}
                        >
                          <Trash2 size={13} />
                        </button>

                        {/* 4. WhatsApp Share Receipt */}
                        <a
                          href={`https://wa.me/91${(p.customer_phone || '').replace(/\D/g, '')}?text=${generateWhatsAppReceiptText(p)}`}
                          target="_blank"
                          rel="noreferrer"
                          title={isMr ? 'व्हॉट्सॲपवर पावती पाठवा' : 'Send WhatsApp Receipt'}
                          style={{
                            padding: '0.35rem 0.55rem',
                            borderRadius: 6,
                            border: 'none',
                            background: '#16a34a',
                            color: '#ffffff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textDecoration: 'none',
                            fontSize: '0.78rem',
                            fontWeight: 800
                          }}
                        >
                          <span>💬</span>
                        </a>

                        <a
                          href={`sms:${(p.customer_phone || '').replace(/\D/g, '')}?body=${generateSmsReceiptText(p)}`}
                          title={isMr ? 'SMS पावती पाठवा' : 'Send SMS Receipt'}
                          style={{
                            padding: '0.35rem 0.55rem',
                            borderRadius: 6,
                            border: 'none',
                            background: '#0284c7',
                            color: '#ffffff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textDecoration: 'none',
                            fontSize: '0.78rem',
                            fontWeight: 800
                          }}
                        >
                          <span>✉️</span>
                        </a>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Printable Payment Receipt Modal */}
      {viewReceipt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            maxWidth: 480,
            width: '100%',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Top Control Bar */}
            <div className="no-print" style={{
              background: '#0f172a',
              color: '#ffffff',
              padding: '0.75rem 1.25rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontWeight: 800, fontSize: '0.9rem' }}>
                {isMr ? 'पेमेंट पावती प्रिव्ह्यू' : 'Payment Receipt Preview'}
              </span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button
                  onClick={() => window.print()}
                  style={{
                    padding: '0.35rem 0.75rem',
                    borderRadius: 6,
                    border: 'none',
                    background: '#0284c7',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    cursor: 'pointer'
                  }}
                >
                  <Printer size={13} />
                  <span>{isMr ? 'प्रिंट करा' : 'Print'}</span>
                </button>
                <button
                  onClick={() => setViewReceipt(null)}
                  style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Printable Thermal Receipt Content */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', fontSize: '0.85rem' }}>
              <div style={{ textAlign: 'center', borderBottom: '2px dashed #cbd5e1', paddingBottom: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#0284c7', textTransform: 'uppercase' }}>
                  {isMr ? '॥ अधिकृत पेमेंट पावती ॥' : '॥ OFFICIAL PAYMENT RECEIPT ॥'}
                </div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 900, color: '#0f172a', margin: '4px 0' }}>
                  {isMr 
                    ? (effectiveTenant?.brandMarathi || effectiveTenant?.brand_name_mr || effectiveTenant?.name || 'समर्थ कृपा डेअरी')
                    : (effectiveTenant?.brandEnglish || effectiveTenant?.brand_name_en || effectiveTenant?.name || 'Samarth Krupa Dairy')}
                </h3>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {effectiveTenant?.phone || '9822012345'} • {effectiveTenant?.address || ''}
                </div>
              </div>

              {/* Receipt Meta */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#475569', marginBottom: '0.85rem' }}>
                <div>
                  <strong>{isMr ? 'पावती क्र:' : 'Receipt #:'}</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{viewReceipt.receipt_no}</span>
                </div>
                <div>
                  <strong>{isMr ? 'दिनांक:' : 'Date:'}</strong> {viewReceipt.payment_date}
                </div>
              </div>

              {/* Customer Info */}
              <div style={{ background: '#f8fafc', padding: '0.65rem 0.85rem', borderRadius: 8, marginBottom: '0.85rem', fontSize: '0.8rem' }}>
                <div><strong>{isMr ? 'ग्राहक नाव:' : 'Customer:'}</strong> {viewReceipt.customer_name_mr || viewReceipt.customer_name} (ID: #{viewReceipt.customer_id})</div>
                <div><strong>{isMr ? 'मार्ग:' : 'Route:'}</strong> {viewReceipt.line_name_mr || viewReceipt.line_name || 'Line'}</div>
                {viewReceipt.customer_phone && <div><strong>{isMr ? 'फोन:' : 'Phone:'}</strong> {viewReceipt.customer_phone}</div>}
              </div>

              {/* Amount Breakdown Table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginBottom: '0.85rem' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 0', color: '#475569' }}>{isMr ? 'पावती पूर्वीची एकूण थकबाकी' : 'Dues at Payment'}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 700 }}>₹{viewReceipt.dues_at_payment}</td>
                  </tr>
                  {parseFloat(viewReceipt.cash_amount) > 0 && (
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 0', color: '#15803d' }}>• {isMr ? 'रोख रक्कम (Cash)' : 'Cash Payment'}</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>₹{viewReceipt.cash_amount}</td>
                    </tr>
                  )}
                  {parseFloat(viewReceipt.upi_amount) > 0 && (
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 0', color: '#0369a1' }}>• {isMr ? 'UPI पेमेंट' : 'UPI Payment'}</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>₹{viewReceipt.upi_amount}</td>
                    </tr>
                  )}
                  {parseFloat(viewReceipt.cheque_amount) > 0 && (
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 0', color: '#7e22ce' }}>
                        • {isMr ? 'चेक पेमेंट' : 'Cheque'} ({viewReceipt.cheque_bank_name} #{viewReceipt.cheque_no})
                      </td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 700, color: '#7e22ce' }}>₹{viewReceipt.cheque_amount}</td>
                    </tr>
                  )}
                  <tr style={{ borderTop: '2px solid #0f172a', fontWeight: 900, fontSize: '1rem' }}>
                    <td style={{ padding: '8px 0', color: '#0f172a' }}>{isMr ? 'एकूण मिळालेली रक्कम' : 'Total Amount Paid'}</td>
                    <td style={{ padding: '8px 0', textAlign: 'right', color: '#16a34a' }}>₹{viewReceipt.total_amount}</td>
                  </tr>
                  <tr style={{ borderTop: '1px dashed #cbd5e1', fontWeight: 800 }}>
                    <td style={{ padding: '6px 0', color: '#64748b' }}>{isMr ? 'उर्वरित बाकी शिल्लक' : 'Remaining Balance'}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', color: viewReceipt.balance_remaining > 0 ? '#b91c1c' : '#15803d' }}>
                      ₹{viewReceipt.balance_remaining}
                    </td>
                  </tr>
                </tbody>
              </table>

              {viewReceipt.remarks && (
                <div style={{ fontSize: '0.74rem', color: '#64748b', fontStyle: 'italic', marginBottom: '1rem' }}>
                  <strong>{isMr ? 'टीप:' : 'Note:'}</strong> {viewReceipt.remarks}
                </div>
              )}

              {/* Signature */}
              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: '1rem', borderTop: '1px dashed #cbd5e1' }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                  {isMr ? 'कम्प्युटर जनरेटेड पावती' : 'Computer Generated Receipt'}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ height: 25 }}></div>
                  <div style={{ borderTop: '1px solid #475569', fontSize: '0.72rem', fontWeight: 700, paddingTop: 2 }}>
                    {isMr ? 'अधिकृत स्वाक्षरी / कॅशियर' : 'Authorized Signature'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
